import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { CalendarCheck, Mountain } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { PageShell } from "../components/landing/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return iso;
  }
};

export const MyReservations = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/enquiries/mine`, { withCredentials: true })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, [user]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" data-testid="myres-loading-spinner" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  return (
    <PageShell title="My Reservations" subtitle={`Everything booked under ${user.email}`}>
      {rows === null ? (
        <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" data-testid="myres-list-spinner" />
      ) : rows.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-100 p-14 text-center" data-testid="myres-empty-state">
          <Mountain className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="mt-4 font-display font-bold text-[#0b132b]">No reservations yet</p>
          <p className="mt-1 text-sm text-slate-400">Browse our tours and book your first Swiss adventure.</p>
          <Link
            to="/"
            data-testid="myres-browse-tours-link"
            className="mt-6 inline-flex items-center rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-3 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)]"
          >
            Browse Tours
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="myres-list">
          {rows.map((e) => (
            <div key={e.id} data-testid={`myres-card-${e.id}`} className="rounded-2xl bg-white border border-slate-100 p-6 shadow-[0_10px_30px_-16px_rgba(11,19,43,0.15)]">
              <div className="flex items-center justify-between gap-3">
                <span
                  className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
                    e.type === "reservation" ? "bg-[var(--brand-soft)] text-[var(--brand)]" : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {e.type}
                </span>
                <span className="text-xs font-semibold text-slate-400">{fmt(e.created_at)}</span>
              </div>
              <p className="mt-4 font-display font-bold text-lg text-[#0b132b]">{e.car || e.tour || "General enquiry"}</p>
              {(e.pickup_date || e.return_date) && (
                <p className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-500">
                  <CalendarCheck className="w-4 h-4 text-[var(--brand)]" />
                  {e.pickup_date || "—"} → {e.return_date || "—"}
                </p>
              )}
              {e.message && <p className="mt-2.5 text-sm text-slate-500 leading-relaxed">{e.message}</p>}
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
};
